import { useOnboardingAnalytics } from "analytics/onboarding";
import {
  getConnectContext,
  useAccountSetupAnalytics,
} from "analytics/accountSetup";
import React, { ReactNode, useState } from "react";
import { KeyRound, Zap } from "lucide-react";
import AntSegmented from "antd/es/segmented";
import styled, { css } from "styled-components";
import Form from "common/Form/Form";
import Input from "common/Inputs/Input";
import CustomInputNumber from "common/Inputs/CustomInputNumber";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import SubmitButton from "features/onboarding/common/SubmitButton";
import QuickConnectForm from "features/configuration/accounts/CreateAccount/QuickConnectForm";
import SelectSubExchange from "features/portfolio/CreateAccount/SelectSubExchange";
import { ClientFormProps } from "features/configuration/common/types";
import useClientForm from "features/configuration/accounts/CreateAccount/useClientForm";
import { DEMO_ACCOUNT_DEFAULT_INITIAL_FUNDS } from "@shared/features/portfolio/constants";
import { validateDemoAccountInitialFunds } from "@shared/features/configuration/utils";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import useMedia from "utils/hooks/useMedia";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import {
  CREATE_ACCOUNT_API_KEY_ID,
  CREATE_ACCOUNT_API_PASSPHRASE_ID,
  CREATE_ACCOUNT_API_SECRET_ID,
  CREATE_ACCOUNT_CONNECT_EXCHANGE_BUTTON_ID,
  CREATE_ACCOUNT_USE_API_KEYS_ID,
  CREATE_ACCOUNT_USE_QUICK_CONNECT_ID,
} from "testIds.ts";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook/layout.ts";
import NewLookConnectHelp, {
  NewLookQuickConnectSteps,
  NewLookSecurityNote,
} from "features/portfolio/CreateAccount/NewLook/NewLookConnectHelp";
import NewLookFormAlert from "features/portfolio/CreateAccount/NewLook/NewLookFormAlert";
import { Hint } from "features/portfolio/CreateAccount/NewLook/styles";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";

export interface InlineFormError {
  description: string;
  title?: string;
}

interface NewLookClientFormProps extends ClientFormProps {
  clientForm: ReturnType<typeof useClientForm>;
  formError?: InlineFormError;
  clearFormError: () => void;
  footer?: ReactNode;
}

const WIDE_MEDIA = "(min-width: 1024px)";

const NewLookClientForm = ({
  account,
  primaryExchange,
  form,
  isOnboarding,
  isQuickConnect,
  clientForm,
  formError,
  clearFormError,
  footer,
}: NewLookClientFormProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const isWide = useMedia([WIDE_MEDIA], [true], false);
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const {
    isEdit,
    showPassphrase,
    onFinish,
    whitelistIPs,
    quotaModal,
    mainButtonData,
    isDemoWorkspace,
    validateApiInfo,
    isLoading,
    openConfirmModal,
    confirmModalContextHolder,
    accountSubExchange,
    isHyperLiquid,
    quickConnectLoading,
  } = clientForm;

  const showQuickConnect = !!isQuickConnect && !isDemoWorkspace && !isEdit;
  // Quick connect first: the API keys path (its guide, fields and IP list)
  // shows only once the user chooses it.
  const [usingKeys, setUsingKeys] = useState(false);
  const trackOnboarding = useOnboardingAnalytics();
  const trackAccountSetup = useAccountSetupAnalytics();
  const keysMode = !showQuickConnect || usingKeys;
  // Onboarding on a wide screen: the form card and, beside it, what helps
  // with the current tab (the IPs and guide, or how quick connect works),
  // in the same place for both tabs.
  const isCard = !!isOnboarding && isWide;

  const getApiRule = (
    fieldName: Parameters<typeof validateApiInfo>[0],
    label: string
  ) => [
    () => ({
      validator() {
        return validateApiInfo(fieldName, label);
      },
    }),
  ];

  const submit = () => {
    clearFormError();
    form.submit();
  };

  const demoFunds = isDemoWorkspace && !isEdit && (
    <Form.Item
      label={t("create_account.initial_funds")}
      name="demo_initial_funds"
      initialValue={DEMO_ACCOUNT_DEFAULT_INITIAL_FUNDS}
      rules={[
        () => ({
          validator(_, value) {
            const error = validateDemoAccountInitialFunds(value);
            if (error) return Promise.reject(error);
            else return Promise.resolve();
          },
        }),
      ]}
    >
      <CustomInputNumber />
    </Form.Item>
  );

  const apiFields = !isDemoWorkspace && (
    <>
      <Form.Item
        label={
          isHyperLiquid
            ? t("create_account.account_wallet_address")
            : t("create_account.api_key")
        }
        name="api_key"
        rules={getApiRule("api_key", t("create_account.api_key"))}
      >
        <Input
          placeholder={account ? account.api_key : ""}
          autoComplete="off"
          spellCheck={false}
          data-testid={CREATE_ACCOUNT_API_KEY_ID}
        />
      </Form.Item>
      <Form.Item
        label={
          isHyperLiquid
            ? t("create_account.api_private_key")
            : t("create_account.api_secret")
        }
        name="api_secret"
        rules={getApiRule("api_secret", t("create_account.api_secret"))}
      >
        <Input.Password
          placeholder={account ? "*********" : ""}
          autoComplete="new-password"
          data-testid={CREATE_ACCOUNT_API_SECRET_ID}
        />
      </Form.Item>
      {showPassphrase && (
        <Form.Item
          label={t("create_account.api_passphrase")}
          name="api_passphrase"
          rules={getApiRule(
            "api_passphrase",
            t("create_account.api_passphrase")
          )}
        >
          <Input.Password
            placeholder={account ? "*********" : ""}
            autoComplete="new-password"
            data-testid={CREATE_ACCOUNT_API_PASSPHRASE_ID}
          />
        </Form.Item>
      )}
    </>
  );

  const help = (
    <NewLookConnectHelp
      primaryExchange={primaryExchange}
      selectedSubExchange={accountSubExchange}
      collapsible={!isCard}
    />
  );

  // The API keys path: its guide, then the key fields and their submit.
  const keysGuide = showQuickConnect && <KeysGuide>{help}</KeysGuide>;
  const securityNote = !isDemoWorkspace && (
    <InlineExtras>
      <NewLookSecurityNote />
    </InlineExtras>
  );
  const keysEntry = (
    <>
      {apiFields}
      {/* Wide, it's beside the form, so the button stays in view. */}
      {!isCard && securityNote}
      <SubmitButton
        onClick={submit}
        loading={mainButtonData.loading}
        disabled={mainButtonData.disabled}
        type="primary"
        submitOnEnter={true}
        style={{ marginTop: 4 }}
        data-testid={CREATE_ACCOUNT_CONNECT_EXCHANGE_BUTTON_ID}
      >
        {showQuickConnect
          ? t("create_account.new_look.connect_with_keys")
          : mainButtonData.text}
      </SubmitButton>
    </>
  );

  const formContent = (
    <FormPanel $framed={isCard}>
      {formError && (
        <NewLookFormAlert
          title={formError.title ?? t("create_account.new_look.error_title")}
          scrollIntoView={true}
        >
          {formError.description}
        </NewLookFormAlert>
      )}
      <Form
        form={form}
        name="clientForm"
        onFinish={onFinish}
        onValuesChange={clearFormError}
        layout="vertical"
        requiredMark={false}
        style={{ width: "100%" }}
      >
        {/* Two ways in, as tabs (like the classic look): quick connect
            first, the API keys path only when chosen. */}
        {showQuickConnect && (
          <MethodTabs
            block={true}
            value={usingKeys ? "keys" : "quick"}
            onChange={(value) => {
              clearFormError();
              setUsingKeys(value === "keys");
              trackAccountSetup("Connect Account - Method Changed", {
                context: getConnectContext(isOnboarding),
                exchange: primaryExchange,
                connect_method: value === "keys" ? "api_keys" : "quick_connect",
              });
              if (isOnboarding)
                trackOnboarding("Connect Method Changed", {
                  exchange: primaryExchange,
                  connect_method:
                    value === "keys" ? "api_keys" : "quick_connect",
                });
            }}
            options={[
              {
                value: "quick",
                label: (
                  <TabLabel data-testid={CREATE_ACCOUNT_USE_QUICK_CONNECT_ID}>
                    <Zap
                      size={14}
                      strokeWidth={NEW_LOOK_ICON_STROKE}
                      aria-hidden
                    />
                    {t("create_account.new_look.quick_connect_title")}
                  </TabLabel>
                ),
              },
              {
                value: "keys",
                label: (
                  <TabLabel data-testid={CREATE_ACCOUNT_USE_API_KEYS_ID}>
                    <KeyRound
                      size={14}
                      strokeWidth={NEW_LOOK_ICON_STROKE}
                      aria-hidden
                    />
                    {t("create_account.new_look.api_keys_title")}
                  </TabLabel>
                ),
              },
            ]}
          />
        )}
        <SelectSubExchange
          primaryExchange={primaryExchange}
          account={account}
          isEdit={isEdit}
          isLoading={isLoading}
        />
        {demoFunds}
        {showQuickConnect && !usingKeys && (
          <>
            <QuickConnectForm
              primaryExchange={primaryExchange}
              openConfirmModal={openConfirmModal}
              explainedBeside={isCard}
            />
          </>
        )}
        {keysMode &&
          (isCard ? (
            keysEntry
          ) : (
            <>
              {keysGuide}
              {/* In the order of the job: the IPs go into the exchange
                  while creating the key, so they come before the key
                  fields. */}
              {!isDemoWorkspace && <IpsFirst>{whitelistIPs}</IpsFirst>}
              {keysEntry}
            </>
          ))}
      </Form>
      {isEdit && !isDemoWorkspace && (
        <Hint>{t("create_account.edit_name_message")}</Hint>
      )}
    </FormPanel>
  );

  return (
    <LoadingSpinnerOverlay
      isLoading={isLoading && !quickConnectLoading}
      hideContentWhileLoading={true}
      wrapStyle={
        isBottomNavigator
          ? { height: "100%", display: "flex", flexDirection: "column" }
          : undefined
      }
    >
      {isCard ? (
        <Page>
          <Main>
            {formContent}
            {footer}
          </Main>
          <Side>
            {keysMode ? (
              <>
                {/* The IPs first: they go into the exchange while creating
                    the key, beside the key fields. */}
                {!isDemoWorkspace && whitelistIPs}
                {help}
                {securityNote}
              </>
            ) : (
              <NewLookQuickConnectSteps primaryExchange={primaryExchange} />
            )}
          </Side>
        </Page>
      ) : (
        <Stack>
          {/* Without quick connect the guide leads the page, as before. */}
          {!showQuickConnect && help}
          {formContent}
          {footer}
        </Stack>
      )}
      {quotaModal}
      {confirmModalContextHolder}
    </LoadingSpinnerOverlay>
  );
};

export default NewLookClientForm;

const Page = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 600px) 360px;
  justify-content: center;
  gap: 24px;
  align-items: start;
`;
const Main = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`;
const Side = styled.div`
  position: sticky;
  top: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`;
const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
`;
const FormPanel = styled.div<{ $framed: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
  ${(p) =>
    p.$framed &&
    css`
      padding: 24px;
      border: ${p.theme.borders.panel};
      box-shadow: ${p.theme.shadows.panel};
      border-radius: ${p.theme.radii.card}px;
      background: ${getNewLookTokens(p.theme).panel};
    `}
  .ant-form-item {
    margin-bottom: 16px;
  }
  .ant-form-item.ant-form-item-with-help {
    margin-bottom: 16px;
  }
  .ant-form-item-with-help .ant-form-item-explain {
    min-height: 0;
    margin-top: 4px;
    font-size: 12px;
    line-height: 16px;
  }
  .ant-form-item-label {
    padding-bottom: 6px;
  }
  .ant-form-item-label > label {
    ${newLookType.label};
    color: ${(p) => getNewLookTokens(p.theme).secondary};
    height: auto;
  }
  .ant-input,
  .ant-input-affix-wrapper {
    min-height: 40px;
  }
  .ant-input-affix-wrapper .ant-input {
    min-height: 0;
  }
`;
const InlineExtras = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 4px 0 20px;
`;
const IpsFirst = styled.div`
  margin-bottom: 16px;
  &:empty {
    display: none;
  }
`;
const KeysGuide = styled.div`
  margin-bottom: 16px;
`;
const MethodTabs = styled(AntSegmented)`
  margin-bottom: 20px;
  .ant-segmented-item-label {
    min-height: 36px;
    line-height: 36px;
    font-size: 13.5px;
    font-weight: 520;
  }
`;
const TabLabel = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
`;
